/**
 * Boli Live - Chat Page
 *
 * Individual message thread for buyer-seller communication.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Send,
  User,
  Loader2,
  Package,
  CheckCheck,
} from 'lucide-react';
import { useAuthStore } from '../lib/stores/auth-store';
import { getMessageService } from '@boli/core';
import type { MessageThread, Message } from '@boli/types';

export default function ChatPage() {
  const { threadId } = useParams<{ threadId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isInitialized, user } = useAuthStore();
  const [thread, setThread] = useState<MessageThread | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isInitialized && !isAuthenticated) {
      navigate('/profile');
    }
  }, [isInitialized, isAuthenticated, navigate]);

  const fetchMessages = useCallback(async () => {
    if (!threadId) return;

    try {
      const messageService = getMessageService();
      const response = await messageService.getMessages(threadId);
      setMessages(response.messages.reverse()); // API returns newest first

      // Get thread info from first fetch
      const threadResponse = await messageService.getThreads();
      const foundThread = threadResponse.threads.find((t: MessageThread) => t.id === threadId);
      if (foundThread) {
        setThread(foundThread);
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setIsLoading(false);
    }
  }, [threadId]);

  const markAsRead = useCallback(async () => {
    if (!threadId) return;

    try {
      const messageService = getMessageService();
      await messageService.markThreadAsRead(threadId);
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  }, [threadId]);

  useEffect(() => {
    if (isAuthenticated && threadId) {
      fetchMessages();
      markAsRead();
    }
  }, [isAuthenticated, threadId, fetchMessages, markAsRead]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !threadId || isSending) return;

    const content = newMessage.trim();
    setNewMessage('');
    setIsSending(true);

    // Optimistically add message
    const optimisticMessage: Message = {
      id: `temp-${Date.now()}`,
      threadId,
      senderId: user?.id || '',
      content,
      isRead: false,
      createdAt: new Date().toISOString(),
    };
    setMessages(prev => [...prev, optimisticMessage]);

    try {
      const messageService = getMessageService();
      const result = await messageService.sendMessage(threadId, content);

      if (result.success && result.data) {
        // Replace optimistic message with real one
        setMessages(prev =>
          prev.map(m => (m.id === optimisticMessage.id ? result.data! : m))
        );
      } else {
        // Remove optimistic message on error
        setMessages(prev => prev.filter(m => m.id !== optimisticMessage.id));
        alert(result.error?.message || 'Failed to send message');
      }
    } catch (error) {
      setMessages(prev => prev.filter(m => m.id !== optimisticMessage.id));
      alert('Failed to send message');
    } finally {
      setIsSending(false);
      inputRef.current?.focus();
    }
  };

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('en-PK', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-PK', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const groupMessagesByDate = () => {
    const groups: { date: string; messages: Message[] }[] = [];
    let currentDate = '';

    for (const message of messages) {
      const msgDate = new Date(message.createdAt).toDateString();
      if (msgDate !== currentDate) {
        currentDate = msgDate;
        groups.push({ date: message.createdAt, messages: [message] });
      } else {
        groups[groups.length - 1].messages.push(message);
      }
    }

    return groups;
  };

  const isBuyer = user?.id === thread?.buyerId;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-border px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Link
          to="/messages"
          className="p-2 hover:bg-secondary rounded-lg transition-colors -ml-2"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>

        <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
          {thread?.otherParty?.avatar ? (
            <img
              src={thread.otherParty.avatar}
              alt={thread.otherParty.name}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <User className="w-5 h-5 text-muted-foreground" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className="font-medium text-foreground truncate">
            {thread?.otherParty?.name || (isBuyer ? 'Seller' : 'Buyer')}
          </p>
          {thread?.order && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Package className="w-3 h-3" />
              <span>{formatCurrency(thread.order.totalAmount)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {messages.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">
              No messages yet. Start the conversation!
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {groupMessagesByDate().map((group, groupIndex) => (
              <div key={groupIndex}>
                {/* Date separator */}
                <div className="flex items-center justify-center mb-4">
                  <span className="px-3 py-1 bg-secondary text-xs text-muted-foreground rounded-full">
                    {formatDate(group.date)}
                  </span>
                </div>

                {/* Messages */}
                <div className="space-y-2">
                  {group.messages.map((message, index) => {
                    const isOwn = message.senderId === user?.id;
                    const showAvatar =
                      !isOwn &&
                      (index === 0 ||
                        group.messages[index - 1].senderId !== message.senderId);

                    return (
                      <motion.div
                        key={message.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`flex items-end gap-2 ${isOwn ? 'justify-end' : 'justify-start'}`}
                      >
                        {!isOwn && (
                          <div className={`w-8 h-8 flex-shrink-0 ${showAvatar ? 'visible' : 'invisible'}`}>
                            <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                              {thread?.otherParty?.avatar ? (
                                <img
                                  src={thread.otherParty.avatar}
                                  alt=""
                                  className="w-8 h-8 rounded-full object-cover"
                                />
                              ) : (
                                <User className="w-4 h-4 text-muted-foreground" />
                              )}
                            </div>
                          </div>
                        )}

                        <div
                          className={`max-w-[70%] px-4 py-2 rounded-2xl ${
                            isOwn
                              ? 'bg-primary text-white rounded-br-md'
                              : 'bg-white border border-border rounded-bl-md'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap break-words">
                            {message.content}
                          </p>
                          <div
                            className={`flex items-center justify-end gap-1 mt-1 text-xs ${
                              isOwn ? 'text-white/70' : 'text-muted-foreground'
                            }`}
                          >
                            <span>{formatTime(message.createdAt)}</span>
                            {isOwn && (
                              <CheckCheck
                                className={`w-3 h-3 ${message.isRead ? 'text-blue-300' : ''}`}
                              />
                            )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input */}
      <div className="bg-white border-t border-border px-4 py-3 sticky bottom-0">
        <form onSubmit={handleSend} className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 px-4 py-3 bg-secondary rounded-full focus:outline-none focus:ring-2 focus:ring-primary"
            disabled={isSending}
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || isSending}
            className="p-3 bg-primary text-white rounded-full hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
